import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import CardsList from "@/components/CardsList";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	playerRoles,
} from "@/utils/constants";
import {getBirthYears} from "@/utils/functions";
import {useStaffs} from "@/utils/hooks";

import "./index.scss";

const ITEMS_PER_PAGE = 20;

function HandleRosterPage(props) {
	const {handleNotification, source, type = "players"} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);

	const [formData, setFormData] = useState({
		elements: [],
	});
	const [elementsConfiguration, setElementsConfiguration] = useState();
	const [loading, setLoading] = useState(false);
	const [roster, setRoster] = useState();

	const [filters, setFilters] = useState({
		// teamStatus: statusOptions.filter((o) => o.default)?.[0],
	});

	const {staffs} = useStaffs({
		setLoading,
		handleNotification: undefined,
		chargeData: type === "staffs",
		prospect: source === "manager" ? false : undefined,
	});

	const [players, setPlayers] = useState([]);
	const [totalItems, setTotalItems] = useState();
	const getPlayers = async (page = 1) => {
		setLoading(true);
		try {
			let response = await PlayerAPI.paginated({
				page,
				pageSize: ITEMS_PER_PAGE,
				prospect: source === "manager" ? false : undefined,
				...filters,
				birthYear: filters?.birthYear ? filters.birthYear.value : undefined,
				role: filters?.role ? filters.role.value : undefined,
				seasonId,
			});
			setPlayers(response.data);
			setTotalItems(response.totalRecords);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const handlePageChanged = (currentPage) => {
		getPlayers(currentPage);
	};
	useEffect(() => {
		if (type === "players") {
			const delayDebounceFn = setTimeout(() => {
				getPlayers();
			}, 500);

			return () => clearTimeout(delayDebounceFn);
		}
	}, [type, filters]);

	const getRoster = async (newId) => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(newId || id);
			let selectedElements = [];
			if (type === "players") {
				selectedElements = Array.from(response?.players?.map((p) => p.player));
			}
			if (type === "staffs") {
				selectedElements = Array.from(response?.staff?.map((s) => s.staff));
			}

			setFormData({...formData, elements: selectedElements});
			setRoster(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		getRoster();
	}, [type]);

	const deleteRoster = async (id) => {
		try {
			await RosterAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const saveRoster = async () => {
		setLoading(true);
		let elements = {};
		if (type === "players") {
			elements = {
				players: formData.elements?.map((p) => {
					return {
						rosterId: id,
						playerId: p.id,
					};
				}),
			};
		}
		if (type === "staffs") {
			elements = {
				staff: formData.elements?.map((s) => {
					return {
						rosterId: id,
						staffId: s.id,
					};
				}),
			};
		}
		try {
			const body = {
				...roster,
				...elements,
			};

			let response;
			if (id) {
				response = await RosterAPI.update(id, body);
			} else {
				response = await RosterAPI.create(body);
			}
			// await deleteRoster(id);
			// const response = await RosterAPI.create(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(
				source === "manager"
					? "/manager/teams/" + (response.id || id)
					: "/development/rosters/" + (response.id || id),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const currentElementsCollection =
		type === "players" ? players : type === "staffs" ? staffs : [];

	const sortArray = (array1) => {
		return array1?.sort((a, b) => {
			return a.lastName.localeCompare(b.lastName);
		});
	};

	useEffect(() => {
		if (type === "players" || type === "staffs") {
			setElementsConfiguration({
				data: sortArray(currentElementsCollection),
				onClick: (element) => {
					let updatedElements = Array.from(formData.elements || []);
					// controllo se il giocatore sia già selezionato oppure no
					if (
						!formData?.elements?.length ||
						formData?.elements?.filter((p) => p.id === element.id)?.length === 0
					) {
						// const isInternalPlayer = !element.prospect;
						updatedElements.push(element);
						setFormData({...formData, elements: updatedElements});
					} else {
						const index = formData.elements.findIndex(
							(p) => p.id === element.id,
						);
						updatedElements.splice(index, 1);
						if (index > -1) {
							setFormData({
								...formData,
								elements: updatedElements,
							});
						}
					}
				},
				selected: (element) =>
					formData?.elements &&
					formData.elements.filter((p) => p.id === element.id)?.length,
				separator: false,
			});
		}
	}, [type, filters, formData, players, staffs]);

	const goBack = () => {
		switch (source) {
			case "manager":
				navigate("/manager/teams/" + id);
				break;
			case "scouting":
				navigate("/development/rosters/" + id);
				break;
			default:
				break;
		}
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveRoster,
			disabled: false,
		},
	];

	const breadcrumbsElements = [
		{
			label: source === "manager" ? "Rose" : "Roster",
			path: source === "manager" ? "/manager/teams" : "/development/rosters",
		},
	];
	breadcrumbsElements.push(
		{label: "/"},
		{
			label: roster
				? source === "manager"
					? roster.name + " " + roster.team.name
					: roster.name
				: "---",
			path:
				source === "manager"
					? "/manager/teams/" + id
					: "/development/rosters/" + id,
		},
	);

	const selectedElementsConfiguration = {
		data: sortArray(formData?.elements || []),
		selected: () => true,
		handleDelete: (element) => {
			let updatedElements = Array.from(formData.elements || []);
			const index = formData.elements.findIndex((p) => p.id === element.id);
			updatedElements.splice(index, 1);
			if (index > -1) {
				setFormData({
					...formData,
					elements: updatedElements,
				});
			}
		},
	};

	const sheetRender = (
		<div className="sheet-container">
			{type === "players" ? (
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Filtri"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6 col-lg-3 padding-small">
										<VLFTextField
											name="search"
											value={filters?.search || ""}
											onChange={(event) => {
												setFilters({...filters, search: event.target.value});
											}}
											label="Giocatore"
											placeholder="Cerca..."
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-3 padding-small">
										<VLFSelectNew
											options={getBirthYears()}
											sorted={false}
											value={filters?.birthYear}
											onChange={(newValue) => {
												setFilters({
													...filters,
													birthYear: newValue,
												});
											}}
											label="Anno di nascita"
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-3 padding-small">
										<VLFSelectNew
											options={playerRoles}
											sorted={false}
											value={filters?.role}
											onChange={(newValue) => {
												setFilters({
													...filters,
													role: newValue,
												});
											}}
											label="Ruolo"
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
			) : null}
			<div className="row no-margin mt-2">
				<div className="col-sm-12 padding-small">
					<div className="list-title">Elementi selezionati</div>
					<div className="list-container selected">
						<CardsList
							type={type === "players" ? "roster_player" : "roster_staff"}
							listConfiguration={selectedElementsConfiguration}
						/>
					</div>
				</div>
			</div>
			<div className="row no-margin mt-2">
				<div className="col-sm-12 padding-small">
					<div className="list-title">Seleziona elementi</div>
					<div
						className={
							"list-container" + (type === "players" ? " paginated" : "")
						}
					>
						<CardsList
							type={type === "players" ? "roster_player" : "roster_staff"}
							listConfiguration={elementsConfiguration}
							totalItems={type === "players" ? totalItems : undefined}
							itemsPerPage={type === "players" ? ITEMS_PER_PAGE : undefined}
							handlePageChanged={
								type === "players" ? handlePageChanged : undefined
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			{sheetRender}
		</Page>
	);
}

export default HandleRosterPage;
